import { useCallback, useMemo, useRef, useState } from 'react';

import type { FiltersValue } from '~features/dashboard-filters';
import { Filters } from '~features/dashboard-filters';

import { ApplicationsExport } from '~entities/application';
import type {
  ApplicationStatus,
  FindApplicationsRequestParams,
} from '~entities/application';

import { nonNullableValue } from '~shared/lib/non-nullable-value/non-nullable-value';
import type {
  DashboardTableRef,
  RowActionOptions,
} from '~shared/ui/dashboard-table';
import { DashboardTable } from '~shared/ui/dashboard-table';
import { Loader } from '~shared/ui/loader';

import Popup from '@platform-ui/popup';

import { ApplicationDetails } from './application-details/application-details';

import { applicationStatusOptions } from '../lib/application-status-options';
import { columns } from '../lib/columns';
import { filtersToParams, sortToParams } from '../lib/search-params-converters';
import {
  colActionOptions,
  colSizingOptions,
  defaultSearchParams,
  defaultState,
} from '../lib/table-options';
import type { ApplicationsTableData } from '../model/applications-table-data';
import { useApplicationsTableDataQuery } from '../model/applications-table-queries';

export const ApplicationsTable = () => {
  const [searchParams, setSearchParams] =
    useState<FindApplicationsRequestParams>();

  const { data, isError, isLoading } =
    useApplicationsTableDataQuery(searchParams);
  const tableRef = useRef<DashboardTableRef>(null);
  const [popupOpened, setPopupOpened] = useState<boolean>(false);
  const lastSelectedApplicationRef = useRef<ApplicationsTableData | null>(null);

  const rowOptions: RowActionOptions<ApplicationsTableData> = useMemo(
    () => ({
      isHoverable: true,
      onClick(item) {
        lastSelectedApplicationRef.current = item;
        setPopupOpened(true);
      },
    }),
    []
  );

  if (isError) {
    // we need to add default errors notifications
  }

  const handleFiltersChange = useCallback(
    (filters: FiltersValue<ApplicationStatus>) => {
      tableRef.current?.setPage(0);
      setSearchParams((oldParams = defaultSearchParams) => ({
        ...oldParams,
        page: 0,
        filter: filtersToParams(filters),
      }));
    },
    []
  );

  const handlePopupClosed = () => {
    setPopupOpened(false);
  };

  return (
    <div className="flex min-h-[226px] flex-col gap-8">
      <Filters
        screen="applications"
        statusOptions={applicationStatusOptions}
        onFiltersChange={handleFiltersChange}
      />
      <div className="relative flex grow flex-col">
        {isLoading && <Loader />}
        {(data?.applications?.length ?? 0) > 0 && (
          <DashboardTable
            screen="applications"
            isPaginationEnabled
            data={data?.applications}
            totalAmount={data?.total}
            columns={columns as any}
            defaultState={defaultState}
            colSizingOptions={colSizingOptions}
            colActionOptions={colActionOptions}
            innerRef={tableRef}
            onStateChange={({ pagination, sort }) =>
              setSearchParams((oldParams) => ({
                ...oldParams,
                page: pagination?.page ?? defaultSearchParams.page,
                perPage: pagination?.rowsPerPage ?? defaultSearchParams.perPage,
                sort: sort && sortToParams(sort),
              }))
            }
            rowActionOptions={rowOptions}
          />
        )}
        {!isLoading && !data?.applications?.length && (
          <div
            className={`
              flex grow items-center justify-center text-center text-base
              text-gray-500
            `}
          >
            We didn&rsquo;t find anything
          </div>
        )}
      </div>
      <div className="flex justify-end">
        {!!searchParams && (
          <ApplicationsExport
            params={searchParams}
            disabled={!data?.applications?.length}
          />
        )}
      </div>
      <Popup
        closeable
        size="s"
        opened={popupOpened}
        onClose={handlePopupClosed}
      >
        <ApplicationDetails
          application={nonNullableValue(lastSelectedApplicationRef.current)}
        />
      </Popup>
    </div>
  );
};
